import { Link } from 'react-router-dom';
import { useTranslation } from '../../../i18n/useTranslation';
import { routes } from '../../../Utils/routes';
import { LegalPage, type LegalSection } from '../LegalPage/LegalPage';

/**
 * Refund Policy. The text is the agreement itself, carried over word for word
 * from the page it replaces: a rewrite during a rebuild would change what
 * people already agreed to.
 *
 * English only, deliberately. This is the version that binds, and translating
 * it would create a second one that might not say the same thing.
 */
const SECTIONS: LegalSection[] = [
  {
    heading: '1. Overview',
    blocks: [
      { kind: 'p', text: (
        <>
          This policy applies to Magbit Premium, a paid subscription for the Magbit tutoring-management service.
          Payments are processed by Paddle, our merchant of record, which handles checkout, billing, and refunds on
          our behalf. Details of plans and prices are listed on our <Link to={routes.pricing}>pricing page</Link>.
        </>
      ) }
    ]
  },
  {
    heading: '2. Free trial',
    blocks: [
      { kind: 'p', text: 'New Premium subscriptions start with a free trial. You will not be charged during the trial, and you can cancel at any time before it ends at no cost. If you do not cancel, the subscription begins automatically at the end of the trial and the plan price is charged.' }
    ]
  },
  {
    heading: '3. 14-day money-back guarantee',
    blocks: [
      { kind: 'p', text: 'If you are not satisfied with Magbit Premium, you may request a full refund of your first subscription payment within 14 days of that first charge, no questions asked. Refunds are issued to the original payment method.' }
    ]
  },
  {
    heading: '4. Renewals',
    blocks: [
      { kind: 'p', text: 'Subscriptions renew automatically (monthly or yearly, depending on your plan). You can cancel at any time from the subscription page inside the app; cancellation stops future charges and your access continues until the end of the period already paid for. If a renewal was charged in error, for example you cancelled before the renewal date but were still billed, contact us within 14 days of the charge and we will refund it in full.' }
    ]
  },
  {
    heading: '5. How to request a refund',
    blocks: [
      { kind: 'p', text: (
        <>
          Email <a href="mailto:rakloze@gmail.com">rakloze@gmail.com</a> from the address associated with your Magbit
          account, or use the “Manage subscription & billing” option inside the app. Refund requests are normally
          handled within 5 business days; once approved, the time for funds to appear depends on your payment
          provider (typically 5–10 business days).
        </>
      ) }
    ]
  },
  {
    heading: '6. After a refund',
    blocks: [
      { kind: 'p', text: 'When a refund is issued, the Premium subscription is cancelled and the account returns to the free plan. Your data (students, lessons, and receipts) remains stored and accessible under the free plan\'s features.' }
    ]
  },
  {
    heading: '7. Statutory rights',
    blocks: [
      { kind: 'p', text: 'Nothing in this policy limits any non-waivable rights you may have under applicable consumer-protection law in your country of residence.' }
    ]
  },
  {
    heading: '8. Contact',
    blocks: [
      { kind: 'p', text: (
        <>For any billing or refund question, contact <a href="mailto:rakloze@gmail.com">rakloze@gmail.com</a>.</>
      ) }
    ]
  }
];

const UPDATED = '23 July 2026';

export function Refunds() {
  const { tr } = useTranslation();
  return (
    <LegalPage
      title="Refund Policy"
      updated={UPDATED}
      sections={SECTIONS}
      back={<Link className="ghost-button" to={routes.home}>{tr('back')}</Link>}
    />
  );
}
